import { createSlice, isAnyOf } from "@reduxjs/toolkit";

import { DashboardAxios } from "../../axios/dashboard/dashboard.axios";
import { DashboardStats } from "../../model/dashboard/DashboardStats";
import { DashboardSetupResponseDto } from "../../model/dashboard/dto/DashboardSetupResponseDto";
import { DashboardTodayResponseDto } from "../../model/dashboard/dto/DashboardTodayResponseDto";
import { GetDashboardStatsDto } from "../../model/dashboard/dto/GetDashboardStatsDto";
import { GetDashboardStatsResponseDto } from "../../model/dashboard/dto/GetDashboardStatsResponseDto";
import { GenericWithUserId } from "../../model/shared/dto/GenericWithUserId";
import { AtiraThunk } from "../AtiraThunk";
import { entryActions } from "../entry/entry.slice";
import { permissionActions } from "../permission/permission.slice";
import { taskActions } from "../task/task.slice";
import { userActions } from "../user/user.slice";
import { revertAllReduxAction } from "../store-reset";

interface DashboardReducer {
  dashboard: DashboardStats;
  setup: DashboardSetupResponseDto | null;
  today: DashboardTodayResponseDto | null;
  todayLoading: boolean;
  todayFailed: boolean;
  /** Bumped when something the dashboard shows may have changed elsewhere,
   * e.g. a task created from the global drawer, so the dashboard refetches */
  dataVersion: number;
}

const initialState = Object.freeze<DashboardReducer>({
  dashboard: {
    entries: {
      categories: { JUNK: 0, LEAD: 0, CONTACT: 0, ENTRY: 0 },
      total: 0,
    },
    reports: { total: 0 },
    forms: { total: 0, categories: { JUNK: 0, ACTIVE: 0, DRAFT: 0 } },
    tasks: { categories: { LEAD: 0, CONTACT: 0, KANBAN: 0 } },
  },
  setup: null,
  today: null,
  todayLoading: false,
  todayFailed: false,
  dataVersion: 0,
});

const getDashboardStats = AtiraThunk<
  GetDashboardStatsResponseDto,
  GetDashboardStatsDto
>("/dashboard/stats", (dto) => DashboardAxios.getDashoardStats(dto));

const getSetup = AtiraThunk<DashboardSetupResponseDto, GenericWithUserId>(
  "/dashboard/setup",
  DashboardAxios.getSetup,
);

const getToday = AtiraThunk<DashboardTodayResponseDto, GenericWithUserId>(
  "/dashboard/today",
  DashboardAxios.getToday,
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {},
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(getDashboardStats.fulfilled, (state, action) => {
      state.dashboard = action.payload;
    });

    addCase(getSetup.fulfilled, (state, action) => {
      state.setup = action.payload;
    });

    addCase(getToday.pending, (state) => {
      state.todayLoading = true;
      state.todayFailed = false;
    });
    addCase(getToday.fulfilled, (state, action) => {
      state.today = action.payload;
      state.todayLoading = false;
    });
    addCase(getToday.rejected, (state) => {
      state.todayLoading = false;
      state.todayFailed = true;
    });

    addMatcher(
      isAnyOf(
        taskActions.createTask.fulfilled,
        taskActions.updateTask.fulfilled,
        entryActions.createNewEntry.fulfilled,
        entryActions.assignEntry.fulfilled,
        permissionActions.inviteMembers.fulfilled,
      ),
      (state) => {
        state.dataVersion += 1;
      },
    );

    addMatcher(isAnyOf(userActions.logout, revertAllReduxAction), (state) => {
      return (state = initialState);
    });
  },
});

export const dashboardActions = {
  getDashboardStats,
  getSetup,
  getToday,
};

export default dashboardSlice.reducer;
